import { FunctionComponent, useEffect } from 'react';
import { ButtonPrimary } from '@spotify-internal/encore-web';
import { useTranslation } from 'react-i18next';

import { getFieldValid, getFieldValue, getRequiredFields } from 'components/ContentReporting/form-utils';
import TextField from 'components/Fields/TextField/TextField';
import analytics, {
  ShowStepEventLabel,
} from 'components/ContentReporting/ContentLegalReporting/report-content-analytics-utils';
import { FormData, FieldOnChangeEvent } from 'containers/ContentReporting/ContentLegalReporting/utils/types';
import { useCurrentMarket } from 'hooks/useCurrentMarket';

import { CLAIMANT_DETAILS } from '../content-legal-reporting-config';

import { STEP_1_FIELDS } from './fields';

type Step1YourDetailsProps = {
  formData: FormData;
  handleFieldChange: (fieldChange: FieldOnChangeEvent) => void;
  onEmailFieldChange: (fieldChange: FieldOnChangeEvent) => void;
  forceValidation: boolean;
  renderErrorMessage: () => void;
  goToNextStep: (requiredFields: string[]) => void;
};

// The payload functions will be used to render in the future so we need to make sure this is kept in sync
export const getPayloadFields = (_fieldValue: (x: any) => any, _market: string) => {
  const fields = STEP_1_FIELDS.map(field => field.fieldName);

  fields.push(CLAIMANT_DETAILS.fields.CLAIMANT_EMAIL, CLAIMANT_DETAILS.fields.CLAIMANT_MORE_INFO);
  return fields;
};

const Step1YourDetails: FunctionComponent<Step1YourDetailsProps> = props => {
  const { handleFieldChange, onEmailFieldChange, forceValidation, formData, goToNextStep, renderErrorMessage } = props;
  const fieldValue = getFieldValue(formData);
  const fieldValid = getFieldValid(formData);

  const { t } = useTranslation('infringement_claim_form');
  const { currentMarket } = useCurrentMarket();

  const requiredFields = getRequiredFields(STEP_1_FIELDS, currentMarket);
  useEffect(() => {
    analytics.trackShowStepEvent(ShowStepEventLabel.Step1ClaimantInformation);
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {STEP_1_FIELDS.map(
        ({
          fieldName,
          fieldLabel,
          required,
          maxLength,
          errorMessageKey = 'infringement_claim_form:field_required',
        }) => (
          <TextField
            field={{
              name: fieldName,
              type: 'text',
              required: required,
              maxlength: maxLength,
              labelOverride: t(fieldLabel),
            }}
            value={fieldValue(fieldName)}
            valid={fieldValid(fieldName)}
            onFieldChange={
              CLAIMANT_DETAILS.fields.CLAIMANT_EMAIL === fieldName ? onEmailFieldChange : handleFieldChange
            }
            forceValidation={forceValidation}
            key={fieldName}
            errorMessageKey={errorMessageKey}
          />
        ),
      )}
      {renderErrorMessage()}
      <ButtonPrimary onClick={() => goToNextStep(requiredFields)}>{t('continue')}</ButtonPrimary>
    </>
  );
};

export default Step1YourDetails;
